import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch(() => setService(false));
    api.get(`/projects?service=${encodeURIComponent(slug)}&limit=3`).catch(() => {});
    api.get(`/projects?limit=3&sort=featured`).then((r) => setProjects(r.data));
  }, [slug]);

  if (service === null) return <div className="container-editorial py-24">Loading…</div>;
  if (!service) return <div className="container-editorial py-24"><h1 className="font-serif text-4xl">Service not found</h1></div>;

  return (
    <div data-testid="page-service-detail" className="bg-[#F9F8F6]">
      <section className="container-editorial pt-16 md:pt-24 pb-12">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#7A736C] mb-6"><Link to="/services" className="hover:text-[#A85C42]">Services</Link> · {service.title}</div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[1] max-w-4xl">{service.title}</h1>
        <p className="mt-8 max-w-2xl text-lg text-[#4A4642] leading-relaxed">{service.short_description}</p>
      </section>

      {service.images?.[0] && (
        <section className="container-editorial pb-16">
          <img src={service.images[0]} alt={service.title} className="w-full aspect-[16/8] object-cover rounded-sm" />
        </section>
      )}

      <section className="container-editorial grid grid-cols-12 gap-10 pb-16 overflow-hidden">
        <div className="col-span-12 md:col-span-5">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#7A736C] mb-4">Overview</div>
          <p className="text-[#4A4642] leading-relaxed whitespace-pre-line">{service.description}</p>
        </div>
        <div className="col-span-12 md:col-span-3 md:col-start-8">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#7A736C] mb-4">Process</div>
          <ol className="space-y-3">
            {service.process?.map((p, i) => (
              <li key={i} className="text-[#2B2927] text-sm border-b border-[#D5D0C8] pb-3"><span className="text-[#A85C42] mr-3">0{i + 1}</span>{p}</li>
            ))}
          </ol>
        </div>
        <div className="col-span-12 md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#7A736C] mb-4">Deliverables</div>
          <ul className="space-y-3">
            {service.deliverables?.map((d, i) => (
              <li key={i} className="text-sm text-[#2B2927] border-b border-[#D5D0C8] pb-3">· {d}</li>
            ))}
          </ul>
        </div>
      </section>

      {service.faqs?.length > 0 && (
        <section className="container-editorial pb-24">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#7A736C] mb-4">FAQs</div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-8">Answers to common questions.</h2>
          <Accordion type="single" collapsible className="border-t border-[#D5D0C8]">
            {service.faqs.map((f, i) => (
              <AccordionItem value={`f-${i}`} key={i} className="border-b border-[#D5D0C8]">
                <AccordionTrigger data-testid={`service-faq-${i}`} className="font-serif text-xl md:text-2xl tracking-tight text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#4A4642] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* Related */}
      <section className="bg-[#EAE6DF] py-24 relative noise">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Related projects</h2>
            <Link to="/portfolio" className="text-sm border-b border-[#2B2927] pb-1 hover:text-[#A85C42]">All projects →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.slice(0, 3).map((p) => (
              <Link key={p.id} to={`/portfolio/${p.slug}`} className="group">
                <div className="aspect-[4/5] overflow-hidden rounded-sm">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                </div>
                <div className="mt-4 font-serif text-2xl tracking-tight">{p.title}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#7A736C]">{p.location}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container-editorial py-24">
        <div className="bg-[#2B2927] text-[#F9F8F6] p-12 md:p-16 rounded-sm">
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Have a project in mind?</h2>
          <Link to="/consultation" className="inline-flex mt-8 px-8 py-4 bg-[#F9F8F6] text-[#2B2927] text-xs uppercase tracking-[0.24em] rounded-sm hover:bg-[#A85C42] hover:text-white transition-colors">Book a consultation</Link>
        </div>
      </section>
    </div>
  );
}
